import {Attendee} from 'Event/attendee'
import {BOLD, CAPS, ITALIC} from 'lib/ui/typography/FontStyleInput'
import {
  CalendarStyles,
  Widget,
  WidgetCalendarProviders,
} from 'Event/Widget/template'
import {logError} from 'Event/Widget/script/helpers/errors'
import {loadGoogleFont} from 'Event/Widget/script/helpers/fonts'
import {
  handleAppleClick,
  handleGoogleCalendarClick,
  handleOutlookClick,
  handleYahooClick,
} from 'Event/Widget/script/helpers/url'
import {
  GOOGLE_ICON,
  APPLE_ICON,
  OUTLOOK_ICON,
  YAHOO_ICON,
} from 'Event/Widget/script/elements/icons'

type ProviderData = {
  backgroundColor: string
  hoverBackgroundColor: string
  hoverTextColor: string
  title: string
  icon: string
}

function getProviderData(provider: WidgetCalendarProviders): ProviderData {
  const providers: Record<string, ProviderData> = {
    google: {
      backgroundColor: '#4285F4',
      hoverBackgroundColor: '#3367D6',
      hoverTextColor: '#FFFFFF',
      title: 'Google',
      icon: GOOGLE_ICON,
    },
    outlook: {
      backgroundColor: '#0072C6',
      hoverBackgroundColor: '#005A9E',
      hoverTextColor: '#FFFFFF',
      title: 'Outlook',
      icon: OUTLOOK_ICON,
    },
    yahoo: {
      backgroundColor: '#400191',
      hoverBackgroundColor: '#29006E',
      hoverTextColor: '#FFFFFF',
      title: 'Yahoo',
      icon: YAHOO_ICON,
    },
    apple: {
      backgroundColor: '#000000',
      hoverBackgroundColor: '#333333',
      hoverTextColor: '#FFFFFF',
      title: 'Apple',
      icon: APPLE_ICON,
    },
  }

  if (!providers[provider]) {
    logError(`Unknown provider: ${provider}`)
    return {
      backgroundColor: '#000000',
      hoverBackgroundColor: '#333333',
      hoverTextColor: '#FFFFFF',
      title: 'Unknown',
      icon: '',
    }
  }

  return providers[provider]
}

export function renderCalendar(
  container: HTMLElement,
  widget: Widget,
  loginUrl: Attendee['login_url'],
) {
  const calendarStyles = widget.template.calendar.styles

  loadGoogleFont(
    `https://fonts.googleapis.com/css2?family=${font(
      calendarStyles,
    )}&display=swap`,
  )

  resetContainer(container)

  const holder = createHolder(widget)
  container.appendChild(holder)

  holder.appendChild(createTitle(widget))

  const buttonsHolder = createButtonsHolder(widget)
  holder.appendChild(buttonsHolder)

  const providers: WidgetCalendarProviders[] = [
    'google',
    'apple',
    'outlook',
    'yahoo',
  ]

  providers.forEach((provider) => {
    renderCalendarButton(buttonsHolder, widget, loginUrl, provider)
  })
}

function renderCalendarButton(
  container: HTMLElement,
  widget: Widget,
  loginUrl: Attendee['login_url'],
  provider: WidgetCalendarProviders,
) {
  if (!widget.template.calendar.providers.includes(provider)) return

  const calendarStyles = widget.template.calendar.styles
  const isIconType = calendarStyles.type === 'icon'

  const button = createButton(widget, provider, isIconType)

  const imageHolder = createImage(widget, provider, isIconType)
  button.appendChild(imageHolder)

  if (!isIconType) {
    button.appendChild(createText(provider))
  }

  button.onclick = () => handleClick(widget, loginUrl, provider)

  container.appendChild(button)
}

function createButton(
  widget: Widget,
  provider: WidgetCalendarProviders,
  isIconType: boolean,
) {
  const calendarStyles = widget.template.calendar.styles
  const button = document.createElement('button')

  const {backgroundColor} = getProviderData(provider)

  applyButtonBaseStyles(button)

  applyButtonTypography(button, calendarStyles)

  applyButtonSizing(button, calendarStyles)

  applyButtonColors(button, calendarStyles, backgroundColor, isIconType)

  applyButtonBorder(button, calendarStyles)

  if (!isIconType) {
    button.style.padding = '0px 10px'
  } else {
    button.style.padding = '0px'
  }

  return button
}

function createImage(
  widget: Widget,
  provider: WidgetCalendarProviders,
  isIconType: boolean,
) {
  const calendarStyles = widget.template.calendar.styles
  const {backgroundColor, icon} = getProviderData(provider)

  const imageHolder = document.createElement('div')
  imageHolder.style.display = 'flex'
  imageHolder.style.justifyContent = 'center'
  imageHolder.style.alignItems = 'center'

  if (!isIconType) {
    imageHolder.style.width = calendarStyles.fontSize + 'px'
  }

  imageHolder.innerHTML = icon

  const svg = imageHolder.querySelector('svg')
  if (svg) {
    if (!isIconType) {
      svg.style.color = '#FFFFFF'
    } else {
      if (calendarStyles.colorScheme === 'custom' && calendarStyles.color) {
        svg.style.color = calendarStyles.color
      } else {
        svg.style.color = backgroundColor
      }
    }

    svg.style.width = '100%'
    svg.style.height = '100%'
  }

  return imageHolder
}

function createText(provider: WidgetCalendarProviders) {
  const {title} = getProviderData(provider)
  const text = document.createElement('div')
  text.style.display = 'flex'
  text.innerHTML = title
  return text
}

function applyButtonBaseStyles(button: HTMLButtonElement) {
  button.style.cursor = 'pointer'
  button.style.display = 'flex'
  button.style.flexDirection = 'row'
  button.style.gap = '8px'
  button.style.alignItems = 'center'
  button.style.justifyContent = 'center'
  button.style.backgroundPosition = 'center'
  button.style.backgroundSize = 'cover'
  button.style.position = 'relative'
  button.style.height = 'auto'
  button.style.minWidth = 'min-content'
  button.style.flex = '1 1 0'
  button.style.transition = 'opacity 0.2s ease-in-out'
  button.style.margin = '0px 4px'
}

function applyButtonTypography(
  button: HTMLButtonElement,
  styles: CalendarStyles,
) {
  button.style.color = '#FFFFFF'
  button.style.fontSize = styles?.fontSize + 'px' || ''
  button.style.fontFamily = font(styles)
  button.style.fontWeight = styles.font?.styles?.includes(BOLD)
    ? 'bold'
    : 'normal'
  button.style.fontStyle = styles.font?.styles?.includes(ITALIC)
    ? 'italic'
    : 'normal'
  button.style.textTransform = styles.font?.styles?.includes(CAPS)
    ? 'uppercase'
    : 'none'
}

function applyButtonSizing(button: HTMLButtonElement, styles: CalendarStyles) {
  button.style.minHeight = styles?.height + 'px' || 'auto'
  button.style.borderRadius = styles?.borderRadius + 'px' || '0px'
}

function applyButtonColors(
  button: HTMLButtonElement,
  styles: CalendarStyles,
  providerColor: string,
  isIconType: boolean,
) {
  if (isIconType) {
    button.style.background = 'transparent'
    return
  }

  if (styles.colorScheme === 'custom' && styles.color) {
    button.style.background = styles.color
  } else {
    button.style.background = providerColor
  }
}

function applyButtonBorder(button: HTMLButtonElement, styles: CalendarStyles) {
  button.style.border = border(styles)

  button.addEventListener('mouseenter', () => {
    button.style.borderColor =
      styles?.hoverBorderColor || button.style.borderColor
  })

  button.addEventListener('mouseleave', () => {
    button.style.borderColor = styles?.borderColor || button.style.borderColor
  })
}

function resetContainer(container: HTMLElement) {
  container.innerHTML = ''
  container.style.width = '100%'
  container.style.display = 'flex'
  container.style.justifyContent = 'center'
  container.style.alignItems = 'center'
}

function createHolder(widget: Widget) {
  const calendarStyles = widget.template.calendar.styles
  const holder = document.createElement('div')

  holder.style.display = 'flex'
  holder.style.flexDirection = 'column'
  holder.style.alignItems = 'center'
  holder.style.justifyContent = 'center'
  holder.style.gap = '15px'
  holder.style.width = calendarStyles.width + 'px'

  return holder
}

function createButtonsHolder(widget: Widget) {
  const calendarStyles = widget.template.calendar.styles
  const div = document.createElement('div')
  div.style.display = 'flex'
  div.style.flexDirection = 'row'

  if (calendarStyles.type !== 'icon') {
    div.style.gap = '10px'
  }

  div.style.width = '100%'
  div.style.flexWrap = 'wrap'
  return div
}

function createTitle(widget: Widget) {
  const calendarStyles = widget.template.calendar.styles
  const title = document.createElement('div')

  title.innerText = widget.template.calendar.label || ''
  title.style.fontWeight = calendarStyles.titleFont?.styles?.includes(BOLD)
    ? 'bold'
    : 'normal'
  title.style.fontSize = calendarStyles?.titleFontSize + 'px' || ''
  title.style.fontFamily = titleFont(calendarStyles)
  title.style.fontStyle = calendarStyles.titleFont?.styles?.includes(ITALIC)
    ? 'italic'
    : 'normal'
  title.style.textTransform = calendarStyles.titleFont?.styles?.includes(CAPS)
    ? 'uppercase'
    : 'none'
  title.style.textAlign = 'center'

  return title
}

function handleClick(
  widget: Widget,
  loginUrl: Attendee['login_url'],
  provider: WidgetCalendarProviders,
) {
  if (provider === 'google') handleGoogleCalendarClick(widget, loginUrl)
  if (provider === 'outlook') handleOutlookClick(widget, loginUrl)
  if (provider === 'yahoo') handleYahooClick(widget, loginUrl)
  if (provider === 'apple') handleAppleClick(widget, loginUrl)
}

function border(styles: CalendarStyles) {
  if (!styles.borderWidth) return 'none'
  return `${styles.borderWidth}px solid ${styles.borderColor || '#000000'}`
}

function font(styles: CalendarStyles) {
  if (!styles?.font?.family || styles.font.family === 'inherit') {
    return 'Rubik'
  }
  return styles.font.family
}

function titleFont(styles: CalendarStyles) {
  if (!styles?.titleFont?.family || styles.titleFont.family === 'inherit') {
    return 'Rubik'
  }
  return styles.titleFont.family
}
